import { useEffect } from 'react';

import { getSessionId } from 'state/amplitude/amplitude-package';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  clearOrderPendingProcessModalExposureSessionsForTests,
  hasHandledOrderPendingProcessModalExposureSession,
  markOrderPendingProcessModalExposureSessionHandled,
} from './order-pending-process-modal-exposure-sessions';

const ORDER_PENDING_PROCESS_MODAL_EXPOSURE_DEDUPE_PREFIX = 'amplitude-session:';

export const getOrderPendingProcessModalExperimentSessionDedupeKey = (sessionId?: string | null) =>
  sessionId ? `${ORDER_PENDING_PROCESS_MODAL_EXPOSURE_DEDUPE_PREFIX}${sessionId}` : undefined;

export const clearOrderPendingProcessModalExperimentExposureSessionsForTests = () => {
  clearOrderPendingProcessModalExposureSessionsForTests();
};

export const useOrderPendingProcessModalExperimentVariant = () => {
  const { getVariantValue } = useExperiments();

  return getVariantValue(RunningExperiments.OrderPendingProcessModal)?.value;
};

/**
 * Treatment enables pending-order persistence, recovery UI, and checkout back-navigation guards.
 * Defaults to control when the variant is unresolved so checkout remains safe if Amplitude is slow.
 */
export const useEnableOrderPendingProcessModalExperiment = () => {
  const variantValue = useOrderPendingProcessModalExperimentVariant();

  return variantValue === 'treatment';
};

/**
 * Logs experiment exposure once per Amplitude session from checkout payment surfaces.
 */
export const useExposeOrderPendingProcessModalExperiment = (shouldExpose = true) => {
  const { logExposure } = useExperiments();
  const variantValue = useOrderPendingProcessModalExperimentVariant();
  const amplitudeSessionId = getSessionId()?.toString();

  useEffect(() => {
    const dedupeKey = getOrderPendingProcessModalExperimentSessionDedupeKey(amplitudeSessionId);

    if (
      !shouldExpose ||
      !variantValue ||
      !dedupeKey ||
      hasHandledOrderPendingProcessModalExposureSession(dedupeKey)
    ) {
      return;
    }

    logExposure(RunningExperiments.OrderPendingProcessModal, { dedupeKey });
    markOrderPendingProcessModalExposureSessionHandled(dedupeKey);
  }, [amplitudeSessionId, logExposure, shouldExpose, variantValue]);
};
